import { useCallback, useEffect, useRef, useState } from 'react'
import format from 'date-fns/format'

const ONE_SECOND = 1000
const TIME_BUFFER = 100

const getEndTime = (secondsAmount: number) =>
  Date.now() + ONE_SECOND * secondsAmount + TIME_BUFFER

const formatDate = (date: number) => format(date, 'mm:ss')

type UseCountdownReturn = {
  timeLeft: string
  restartCountdown: VoidFunction
  isFinished: boolean
}

export const useCountdown = (secondsAmount: number = 60): UseCountdownReturn => {
  const [endTime, setInitialTime] = useState<number>(getEndTime(secondsAmount))
  const [timeLeft, setTimeLeft] = useState<string>(formatDate(endTime - Date.now()))
  const descriptionInterval = useRef<number>()

  const handleTimeChange = useCallback(() => {
    const timeDifference = Math.max(endTime - Date.now(), 0)

    if (timeDifference < ONE_SECOND) {
      clearInterval(descriptionInterval.current)
    }

    setTimeLeft(formatDate(timeDifference))
  }, [endTime, setTimeLeft])

  useEffect(() => {
    descriptionInterval.current = window.setInterval(handleTimeChange, ONE_SECOND)

    return () => {
      clearInterval(descriptionInterval.current)
    }
  }, [handleTimeChange])

  const restartCountdown = useCallback(() => {
    const newEndTime = getEndTime(secondsAmount)

    setInitialTime(newEndTime)
    setTimeLeft(formatDate(newEndTime - Date.now()))
  }, [secondsAmount])

  return {
    timeLeft,
    restartCountdown,
    isFinished: endTime - Date.now() < ONE_SECOND,
  }
}
